import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { label } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Send, MessagesSquare, Home, Users } from "lucide-react";

export default function Messages() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [people, setPeople] = useState([]);
  const [properties, setProperties] = useState([]);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ kind: "direct", person: "", property_id: "", title: "" });
  const endRef = useRef(null);

  const loadConvs = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConvs(data);
    return data;
  }, []);

  useEffect(() => {
    loadConvs();
    api.get("/messaging/people").then(({ data }) => setPeople(data));
    api.get("/properties").then(({ data }) => setProperties(data)).catch(() => {});
  }, [loadConvs]);

  const openConv = useCallback(async (conv) => {
    setActive(conv);
    const { data } = await api.get(`/conversations/${conv._id}/messages`);
    setMessages(data);
    loadConvs();
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [loadConvs]);

  const send = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !active) return;
    setDraft("");
    try {
      const { data } = await api.post(`/conversations/${active._id}/messages`, { body });
      setMessages((m) => [...m, data]);
      loadConvs();
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send message");
    }
  };

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/conversations", {
        kind: form.kind,
        participant_ids: form.person ? [form.person] : [],
        property_id: form.kind === "property" ? form.property_id : null,
        title: form.title,
      });
      setOpen(false);
      setForm({ kind: "direct", person: "", property_id: "", title: "" });
      const list = await loadConvs();
      openConv(list.find((cv) => cv._id === data._id) || data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start conversation");
    }
    setBusy(false);
  };

  if (!can(user, "message:read")) return null;

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">One place to talk</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Messages</h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            Owners, cleaners, vendors and managers — all in Cornerstone Ops, all on the record.
          </p>
        </div>
        <Button data-testid="new-conversation-btn" onClick={() => setOpen(true)}
          className="bg-royal hover:bg-royal-deep text-white gap-2 h-11">
          <Plus className="h-4 w-4" /> New conversation
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[300px_1fr]">
        <div className="rounded-lg border border-border bg-white divide-y divide-border overflow-hidden max-h-[520px] overflow-y-auto"
             data-testid="conversation-list">
          {convs.length === 0 && (
            <p className="p-5 text-sm text-muted-foreground">No conversations yet. Start one.</p>
          )}
          {convs.map((cv) => (
            <button key={cv._id} data-testid={`conversation-${cv._id}`} onClick={() => openConv(cv)}
              className={`w-full text-left px-4 py-3.5 transition-colors duration-150 ${
                active?._id === cv._id ? "bg-royal/5" : "hover:bg-secondary/40"}`}>
              <div className="flex items-center gap-2">
                {cv.kind === "property" ? <Home className="h-3.5 w-3.5 text-gold shrink-0" />
                  : cv.kind === "group" ? <Users className="h-3.5 w-3.5 text-gold shrink-0" />
                  : <MessagesSquare className="h-3.5 w-3.5 text-gold shrink-0" />}
                <p className="text-sm font-semibold text-royal-deep truncate">{cv.display_name}</p>
                {cv.unread > 0 && (
                  <span data-testid={`conversation-unread-${cv._id}`}
                    className="ml-auto min-w-[18px] rounded-full bg-gold px-1 text-[10px] font-bold leading-[18px] text-royal-deep text-center">
                    {cv.unread}
                  </span>
                )}
              </div>
              {cv.last_message && (
                <p className="text-xs text-muted-foreground mt-1 truncate">
                  {cv.last_message.sender_name}: {cv.last_message.body}
                </p>
              )}
            </button>
          ))}
        </div>

        <div className="rounded-lg border border-border bg-white flex flex-col min-h-[420px]" data-testid="thread-panel">
          {!active ? (
            <div className="m-auto text-center p-8">
              <MessagesSquare className="h-7 w-7 text-gold mx-auto" />
              <p className="font-display text-lg font-semibold text-royal-deep mt-3">Pick a conversation</p>
              <p className="text-sm text-muted-foreground mt-1">Or start a new one with anyone in your company.</p>
            </div>
          ) : (
            <>
              <div className="border-b border-border px-4 py-3">
                <p className="font-display font-semibold text-royal-deep" data-testid="thread-title">
                  {active.display_name}
                </p>
                <p className="text-xs text-muted-foreground">
                  {label(active.kind)} · {active.participants?.map((p) => p.name).join(", ")}
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[380px]" data-testid="thread-messages">
                {messages.length === 0 && (
                  <p className="text-sm text-muted-foreground">No messages yet — say hello.</p>
                )}
                {messages.map((m) => (
                  <div key={m._id} data-testid={`message-${m._id}`}
                    className={`max-w-[80%] rounded-lg px-3.5 py-2.5 ${
                      m.mine ? "ml-auto bg-royal text-white" : "bg-secondary text-royal-deep"}`}>
                    {!m.mine && <p className="text-[11px] font-semibold text-royal mb-0.5">{m.sender_name}</p>}
                    <p className="text-sm whitespace-pre-line">{m.body}</p>
                    <p className={`text-[10px] mt-1 ${m.mine ? "text-blue-100/70" : "text-muted-foreground"}`}>
                      {new Date(m.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="border-t border-border p-3 flex gap-2" data-testid="message-form">
                <Input data-testid="message-input" value={draft} onChange={(e) => setDraft(e.target.value)}
                  placeholder="Write a message…" className="h-11" />
                <Button data-testid="send-message-btn" type="submit"
                  className="h-11 bg-gold hover:bg-gold/90 text-royal-deep font-semibold gap-2">
                  <Send className="h-4 w-4" /> Send
                </Button>
              </form>
            </>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-conversation-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">Start a conversation</DialogTitle>
            <DialogDescription>
              Message one person directly, or open a thread tied to a property so its history stays together.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={start} className="space-y-4" data-testid="new-conversation-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
                <SelectTrigger data-testid="conversation-kind-select" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct" data-testid="kind-direct">Direct message</SelectItem>
                  <SelectItem value="group" data-testid="kind-group">Group thread</SelectItem>
                  <SelectItem value="property" data-testid="kind-property">Property thread</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.kind === "property" ? (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Property</Label>
                <Select value={form.property_id} onValueChange={(v) => setForm((f) => ({ ...f, property_id: v }))}>
                  <SelectTrigger data-testid="conversation-property-select" className="mt-1.5 h-11">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Person</Label>
                <Select value={form.person} onValueChange={(v) => setForm((f) => ({ ...f, person: v }))}>
                  <SelectTrigger data-testid="conversation-person-select" className="mt-1.5 h-11">
                    <SelectValue placeholder="Who are you messaging?" />
                  </SelectTrigger>
                  <SelectContent>
                    {people.map((p) => (
                      <SelectItem key={p._id} value={p._id} data-testid={`person-option-${p._id}`}>
                        {p.name} · {label(p.role)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.kind === "group" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Thread name</Label>
                <Input data-testid="conversation-title-input" value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="mt-1.5 h-11" />
              </div>
            )}
            <Button data-testid="start-conversation-btn" type="submit" disabled={busy}
              className="w-full h-11 bg-royal hover:bg-royal-deep text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start conversation"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
